import { FC, useMemo } from 'react'
import classNames from 'classnames'

import { Image, Text, useMediaQuery } from '@lounge-fe/ui-kit'

import { BasketLineProps } from '../types'
import { Details } from './Details'
import { BasketLineActionBar } from './BasketLineActionBar'
import { useCartPlugin } from '@/features/cart/hooks/useCartPlugin'
import { GiftWrapPlugin } from '@/features/cart/plugins/GiftWrapPlugin/GiftWrapPlugin'

export const BasketLine: FC<BasketLineProps> = ({
  id,
  title: lineTitle,
  items,
  isSet,
  finalLinePrice = 0,
  originalLinePrice = 0,
  className,
  setCount = 1,
  allItemsKeys,
  allItems,
}) => {
  const plugin = useCartPlugin()
  const isDesktop = useMediaQuery(({ screens }) => `(min-width: ${screens.sm})`)
  const lineItemKeys = useMemo(
    () => allItemsKeys || items.map(({ key }) => key),
    [allItemsKeys, items],
  )
  const url = items[0]?.url

  const isGiftWrap = useMemo(() => {
    const giftWrapPlugin = plugin(GiftWrapPlugin)
    return (
      giftWrapPlugin?.enabled && giftWrapPlugin?.getProduct().id === items[0].id
    )
  }, [items, plugin])

  const showQuantity = useMemo(() => !isGiftWrap, [isGiftWrap])

  return (
    <div
      className={classNames('flex flex-col w-full', className)}
      data-testid={`line-${id}`}
    >
      {lineTitle && (
        <Text variant="body-md" className="mb-3">
          {lineTitle}
        </Text>
      )}
      <div className="flex flex-col gap-y-4">
        {items.map(
          (
            {
              id: variantId,
              productId,
              title,
              color,
              size,
              featuredImage,
              discountAllocations,
              hasOnlyDefaultVariant,
              properties,
              tags,
              giftCard,
            },
            index,
          ) => {
            const isLast = index === items.length - 1
            const actionBarProps = {
              isDesktop,
              isSet,
              lineItemKeys,
              productId: isSet ? items[0].productId : productId,
              quantity: setCount,
              giftCard,
              showQuantity,
            }
            return (
              <div
                key={variantId}
                className="flex flex-col gap-2"
                data-testid={`line-item-${productId}`}
              >
                <div className="flex flex-row gap-x-4">
                  <a href={url}>
                    {featuredImage ? (
                      <Image
                        data={{
                          src: featuredImage.url,
                          width: 108,
                          height: 108,
                          crop: 'center',
                        }}
                        alt={featuredImage.alt}
                        className="object-cover w-[108px] h-full aspect-square"
                      />
                    ) : (
                      <div className="bg-surface-primary-default object-cover w-[108px] h-[108px] aspect-square"></div>
                    )}
                  </a>
                  <div className="flex flex-col items-start justify-between flex-1">
                    <Details
                      url={url}
                      isDesktop={isDesktop}
                      originalLinePrice={originalLinePrice}
                      finalLinePrice={finalLinePrice}
                      title={title}
                      color={color}
                      size={size}
                      isSet={isSet}
                      discountAllocations={discountAllocations}
                      hasOnlyDefaultVariant={hasOnlyDefaultVariant}
                      properties={properties}
                      tags={tags}
                      allItems={allItems}
                    />
                    {isLast && !showQuantity && (
                      <BasketLineActionBar {...actionBarProps} />
                    )}
                  </div>
                </div>
                {isLast && showQuantity && (
                  <BasketLineActionBar {...actionBarProps} />
                )}
              </div>
            )
          },
        )}
      </div>
    </div>
  )
}
